import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';

export type AdminThemeMode = 'light' | 'dark';

const ADMIN_THEME_STORAGE_KEY = 'wanjianshi.admin.theme';

interface AdminThemeModeContextValue {
  mode: AdminThemeMode;
  setMode: (mode: AdminThemeMode) => void;
}

const AdminThemeModeContext = createContext<AdminThemeModeContextValue | null>(null);

function readStoredThemeMode(): AdminThemeMode {
  try {
    return window.localStorage.getItem(ADMIN_THEME_STORAGE_KEY) === 'dark'
      ? 'dark'
      : 'light';
  } catch {
    return 'light';
  }
}

function applyThemeMode(mode: AdminThemeMode) {
  document.documentElement.dataset.theme = mode;
  document.documentElement.style.colorScheme = mode;
}

export function initializeAdminThemeMode() {
  applyThemeMode(readStoredThemeMode());
}

export function AdminThemeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<AdminThemeMode>(readStoredThemeMode);

  const setMode = useCallback((nextMode: AdminThemeMode) => {
    setModeState(nextMode);
    applyThemeMode(nextMode);
    try {
      window.localStorage.setItem(ADMIN_THEME_STORAGE_KEY, nextMode);
    } catch {
      // 隐私模式下存储可能不可用，当前会话仍可正常切换。
    }
  }, []);

  useLayoutEffect(() => {
    applyThemeMode(mode);
  }, [mode]);

  const value = useMemo(() => ({ mode, setMode }), [mode, setMode]);
  return (
    <AdminThemeModeContext.Provider value={value}>
      {children}
    </AdminThemeModeContext.Provider>
  );
}

export function useAdminThemeMode(): AdminThemeModeContextValue {
  const context = useContext(AdminThemeModeContext);
  if (!context) throw new Error('useAdminThemeMode 必须在 AdminThemeModeProvider 内使用');
  return context;
}
